import {
  ContentItem,
  EventContentPlanItem,
  EventCurrency,
  EventOperationsState,
  GuidedAnswerValue,
  GuidedEventSetup,
  RegistrationFormField,
} from '../types';

export type GuidedStepKind = 'text' | 'textarea' | 'date' | 'time' | 'number' | 'choice' | 'multi' | 'tickets' | 'program' | 'participants' | 'products' | 'landing' | 'form' | 'content' | 'review';

export interface GuidedStepOption {
  label: string;
  value: string;
  description?: string;
}

export interface GuidedStepDefinition {
  id: string;
  phase: number;
  eyebrow: string;
  title: string;
  help: string;
  kind: GuidedStepKind;
  placeholder?: string;
  options?: GuidedStepOption[];
  required?: boolean;
}

const nowIso = () => new Date().toISOString();

export const EVENT_SETUP_PHASES = [
  'Tu negocio',
  'Oferta',
  'Página pública',
  'Formulario',
  'Pagos',
  'Clientes',
  'Autoridad',
  'Captación',
  'Contenido',
  'Activación',
] as const;

const baseSteps: GuidedStepDefinition[] = [
  { id: 'event-type', phase: 1, eyebrow: 'Tipo de negocio', title: '¿Qué quieres vender o gestionar?', help: 'Adaptaremos el recorrido, la página, el formulario y el cobro a tu respuesta.', kind: 'choice', required: true, options: [
    { label: 'Servicio profesional', value: 'service', description: 'Consultoría, asesoría, salud, legal u otro servicio' },
    { label: 'Agencia o estudio', value: 'agency', description: 'Servicios recurrentes o por proyecto' },
    { label: 'Curso o programa', value: 'program', description: 'Formación, mentoría o capacitación' },
    { label: 'Producto o membresía', value: 'product', description: 'Venta única o suscripción' },
    { label: 'Evento o experiencia', value: 'event', description: 'Congreso, webinar, taller o encuentro' },
    { label: 'Otro negocio', value: 'other' },
  ] },
  { id: 'event-name', phase: 1, eyebrow: 'Empecemos por lo esencial', title: '¿Cómo se llama tu negocio, servicio u oferta?', help: 'Usaremos este nombre en la página y podrás cambiarlo después.', kind: 'text', placeholder: 'Ej. Consultoría Financiera Norte', required: true },
  { id: 'organizer-name', phase: 1, eyebrow: 'Responsable', title: '¿Quién ofrece este servicio o producto?', help: 'Puede ser una persona, empresa, institución o equipo.', kind: 'text', placeholder: 'Nombre de la marca o profesional', required: true },
  { id: 'objective', phase: 1, eyebrow: 'Resultado esperado', title: '¿Qué resultado quieres generar?', help: 'Describe el cambio principal para el cliente y para tu negocio.', kind: 'textarea', placeholder: 'Ej. Ayudar a empresas a ordenar sus finanzas y generar oportunidades calificadas', required: true },
  { id: 'start-date', phase: 1, eyebrow: 'Fecha de inicio', title: '¿Qué día comienza?', help: 'Si aún no está confirmada, puedes elegir una fecha tentativa.', kind: 'date', required: true },
  { id: 'start-time', phase: 1, eyebrow: 'Horario de inicio', title: '¿A qué hora comienza?', help: 'Usaremos la zona horaria del país de la actividad.', kind: 'time', required: true },
  { id: 'end-date', phase: 1, eyebrow: 'Fecha de finalización', title: '¿Qué día termina?', help: 'Puede ser el mismo día o una fecha posterior.', kind: 'date', required: true },
  { id: 'end-time', phase: 1, eyebrow: 'Horario de finalización', title: '¿A qué hora termina?', help: 'Ayuda a comunicar una agenda clara.', kind: 'time', required: true },
  { id: 'modality', phase: 1, eyebrow: 'Modalidad', title: '¿Cómo se prestará o entregará?', help: 'Solo preguntaremos los datos que correspondan.', kind: 'choice', required: true, options: [
    { label: 'Presencial', value: 'presential', description: 'En una sede física' }, { label: 'Online', value: 'online', description: 'Por una plataforma virtual' }, { label: 'Híbrido', value: 'hybrid', description: 'Presencial y online' },
  ] },
  { id: 'country', phase: 1, eyebrow: 'País', title: '¿En qué país operas o vendes?', help: 'Se usa para pagos, moneda y ubicación.', kind: 'text', placeholder: 'Ej. Argentina', required: true },
  { id: 'city', phase: 1, eyebrow: 'Ciudad', title: '¿En qué ciudad atiendes?', help: 'Solo la pedimos cuando existe atención presencial.', kind: 'text', placeholder: 'Ej. Buenos Aires', required: true },
  { id: 'venue', phase: 1, eyebrow: 'Lugar', title: '¿Cómo se llama el lugar o local?', help: 'Nombre de la sede, hotel, universidad, estudio o espacio.', kind: 'text', placeholder: 'Ej. Centro de Convenciones', required: true },
  { id: 'exact-address', phase: 1, eyebrow: 'Ubicación exacta', title: '¿Cuál es la dirección exacta?', help: 'Calle, número, piso, salón y cualquier referencia útil.', kind: 'text', placeholder: 'Ej. Av. Corrientes 1234, Salón Norte', required: true },
  { id: 'virtual-platform', phase: 1, eyebrow: 'Transmisión', title: '¿Qué plataforma virtual usarás?', help: 'Puedes escribir Zoom, Meet, una URL o dejarlo pendiente.', kind: 'text', placeholder: 'Ej. Zoom o https://…', required: true },
  { id: 'currency', phase: 1, eyebrow: 'Monedas', title: '¿En qué monedas aceptarás pagos?', help: 'Puedes elegir varias. La primera será la moneda principal.', kind: 'multi', required: true, options: [
    { label: 'Peso argentino (ARS)', value: 'ARS' }, { label: 'Dólar (USD)', value: 'USD' }, { label: 'Euro (EUR)', value: 'EUR' }, { label: 'Real (BRL)', value: 'BRL' }, { label: 'Peso chileno (CLP)', value: 'CLP' }, { label: 'Peso colombiano (COP)', value: 'COP' }, { label: 'Peso mexicano (MXN)', value: 'MXN' },
  ] },
  { id: 'event-focus', phase: 1, eyebrow: 'Diferenciadores', title: '¿Qué quieres destacar de tu propuesta?', help: 'Elige todo lo que corresponda. Lo usaremos para redactar la página.', kind: 'multi', required: false, options: [
    { label: 'Atención personalizada', value: 'Atención personalizada' }, { label: 'Resultados medibles', value: 'Resultados medibles' }, { label: 'Experiencia comprobada', value: 'Experiencia comprobada' }, { label: 'Metodología propia', value: 'Metodología propia' }, { label: 'Acompañamiento', value: 'Acompañamiento' }, { label: 'Comunidad', value: 'Comunidad' },
  ] },
  { id: 'description', phase: 1, eyebrow: 'Presentación', title: '¿Cómo describirías lo que ofreces?', help: 'Incluye para quién es, qué problema resuelve, cómo funciona y qué resultado puede esperar el cliente.', kind: 'textarea', placeholder: 'Explica la propuesta de valor en palabras simples.', required: true },
  { id: 'tickets', phase: 2, eyebrow: 'Oferta', title: 'Crea tus planes, paquetes o formas de compra', help: 'Agrega una por vez. Pueden ser gratis, pagas, privadas o requerir aprobación.', kind: 'tickets', required: true },
  { id: 'program', phase: 3, eyebrow: 'Programa y protagonistas', title: 'Construye la agenda y vincula cada charla con su speaker', help: 'Agrega speakers, cursos, talleres, paneles y conferencias con día, horario y sala.', kind: 'program', required: false },
  { id: 'participants', phase: 3, eyebrow: 'Ecosistema de la oferta', title: 'Selecciona aliados, partners o marcas participantes', help: 'Solo se mostrarán públicamente las organizaciones que elijas para esta oferta.', kind: 'participants', required: false },
  { id: 'landing', phase: 3, eyebrow: 'Página pública', title: 'Revisa el borrador de tu página de venta', help: 'Lo generamos con tus respuestas. Nada se publica sin tu aprobación.', kind: 'landing', required: true },
  { id: 'has-products', phase: 4, eyebrow: 'Cursos y productos', title: '¿Las personas podrán elegir un curso, programa o producto?', help: 'Preguntamos esto antes de diseñar el formulario para incluir la selección correcta.', kind: 'choice', required: true, options: [
    { label: 'Sí, quiero ofrecerlos', value: 'yes' }, { label: 'No, ya definí mi oferta', value: 'no' }, { label: 'Lo definiré después', value: 'later' },
  ] },
  { id: 'products', phase: 4, eyebrow: 'Oferta adicional', title: 'Agrega tus cursos, programas o productos', help: 'Puedes ofrecer más de uno y definir su precio y monedas.', kind: 'products', required: true },
  { id: 'registration-form', phase: 4, eyebrow: 'Formulario', title: '¿Qué datos necesitas de cada prospecto o cliente?', help: 'Partimos de campos recomendados; puedes ordenarlos y desactivar los que no necesites.', kind: 'form', required: true },
  { id: 'accepts-payments', phase: 5, eyebrow: 'Cobros', title: '¿Necesitas cobrar desde Grosym?', help: 'Si cobras por otra vía o tu oferta es gratuita, puedes omitir este paso.', kind: 'choice', required: true, options: [
    { label: 'Sí, necesito cobrar', value: 'yes' }, { label: 'No, será gratuito', value: 'no' }, { label: 'Lo decidiré después', value: 'later' },
  ] },
  { id: 'payment-method', phase: 5, eyebrow: 'Medios de pago', title: '¿Cómo quieres recibir los pagos?', help: 'Puedes elegir varias opciones. Solo mostraremos una integración como conectada cuando realmente lo esté.', kind: 'multi', required: true, options: [
    { label: 'Mercado Pago', value: 'mercado_pago', description: 'Recomendado para Argentina' }, { label: 'Stripe', value: 'stripe' }, { label: 'Enlace o transferencia manual', value: 'manual' }, { label: 'Configurar después', value: 'later' },
  ] },
  { id: 'manual-payment-url', phase: 5, eyebrow: 'Enlace de cobro', title: 'Pega el enlace o las instrucciones de pago', help: 'Se mostrará durante la inscripción.', kind: 'text', placeholder: 'https://… o instrucciones breves', required: true },
  { id: 'has-contacts', phase: 6, eyebrow: 'Clientes', title: '¿Ya tienes una lista de prospectos o clientes?', help: 'Puedes comenzar sin una base e importarla más adelante.', kind: 'choice', required: true, options: [
    { label: 'Sí, quiero importarla', value: 'yes' }, { label: 'No todavía', value: 'no' },
  ] },
  { id: 'contact-source', phase: 6, eyebrow: 'Origen de datos', title: '¿Dónde está tu lista?', help: 'Después podrás mapear columnas, revisar duplicados y confirmar.', kind: 'choice', required: true, options: [
    { label: 'CSV o Excel', value: 'file' }, { label: 'Google Sheets', value: 'google_sheets' }, { label: 'La cargaré a mano', value: 'manual' },
  ] },
  { id: 'organizer-bio', phase: 7, eyebrow: 'Confianza', title: '¿Por qué deberían elegirte?', help: 'Incluye experiencia, resultados, método o respaldo institucional.', kind: 'textarea', placeholder: 'Ej. Más de 10 años ayudando a empresas…', required: true },
  { id: 'audience', phase: 8, eyebrow: 'Audiencia', title: '¿A quién necesitas convocar?', help: 'Sé concreto: rol, sector, ubicación o problema.', kind: 'textarea', placeholder: 'Ej. Directores de RR. HH. de empresas medianas en Argentina', required: true },
  { id: 'channels', phase: 8, eyebrow: 'Canales', title: '¿Por dónde llegarás a esas personas?', help: 'Elige todos los canales que realmente puedas activar.', kind: 'multi', required: true, options: [
    { label: 'WhatsApp', value: 'WhatsApp' }, { label: 'Email', value: 'Email' }, { label: 'LinkedIn', value: 'LinkedIn' }, { label: 'Instagram', value: 'Instagram' }, { label: 'Publicidad', value: 'Ads' }, { label: 'Aliados', value: 'Allies' },
  ] },
  { id: 'ad-budget', phase: 8, eyebrow: 'Publicidad', title: '¿Cuánto planeas invertir en anuncios?', help: 'Ingresa el monto y elige la moneda. Puedes cambiar ambos después.', kind: 'number', placeholder: '0', required: false },
  { id: 'allies', phase: 8, eyebrow: 'Alianzas', title: '¿Qué aliados podrían ayudarte a convocar?', help: 'Separa nombres con comas. Puedes dejarlo pendiente.', kind: 'text', placeholder: 'Cámaras, universidades, empresas…', required: false },
  { id: 'primary-action', phase: 8, eyebrow: 'Conversión', title: '¿Cuál es la acción más importante?', help: 'Será el foco del plan de convocatoria.', kind: 'choice', required: true, options: [
    { label: 'Contratar o comprar', value: 'buy' }, { label: 'Agendar una llamada', value: 'schedule' }, { label: 'Pedir una propuesta', value: 'apply' }, { label: 'Completar un formulario', value: 'register' },
  ] },
  { id: 'content-plan', phase: 9, eyebrow: 'Contenido', title: 'Tu plan inicial ya está listo', help: 'Lo organizamos por función: autoridad, convocatoria, conversión y seguimiento.', kind: 'content', required: true },
  { id: 'review', phase: 10, eyebrow: 'Última revisión', title: 'Revisa lo necesario antes de activar', help: 'Lo opcional nunca bloqueará tu operación.', kind: 'review', required: true },
];

export function createRecommendedRegistrationFields(): RegistrationFormField[] {
  return [
    { id: 'firstName', label: 'Nombre', type: 'text', required: true, active: true, sortOrder: 0 },
    { id: 'lastName', label: 'Apellido', type: 'text', required: true, active: true, sortOrder: 1 },
    { id: 'email', label: 'Email', type: 'text', required: true, active: true, sortOrder: 2 },
    { id: 'whatsapp', label: 'WhatsApp', type: 'text', required: false, active: true, sortOrder: 3 },
    { id: 'country', label: 'País', type: 'text', required: false, active: true, sortOrder: 4 },
    { id: 'profession', label: 'Profesión o rol', type: 'text', required: false, active: true, sortOrder: 5 },
    { id: 'privacy', label: 'Acepto el uso de mis datos para gestionar mi solicitud o compra', type: 'checkbox', required: true, active: true, sortOrder: 6 },
  ];
}

export function createBlankEventOperationsState(timestamp = nowIso(), id = `event-${Date.now()}`): EventOperationsState {
  return {
    version: 2,
    dataSource: 'provider',
    event: {
      id,
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      timezone: 'America/Argentina/Buenos_Aires',
      venue: '',
      city: '',
      country: '',
      capacity: 0,
      status: 'draft',
      defaultCurrency: 'ARS',
      acceptedCurrencies: ['ARS'],
      createdAt: timestamp,
      updatedAt: timestamp,
    },
    attendees: [], ticketTypes: [], orders: [], payments: [], scholarships: [], speakers: [], sessions: [], rooms: [], tracks: [], campaigns: [], checkIns: [], sessionRegistrations: [], communications: [], coupons: [], waitlist: [], activityLogs: [],
    registrationFields: createRecommendedRegistrationFields(),
    products: [],
    eventSponsorIds: [],
    partners: [],
    integrations: [],
    contentPlan: [],
    updatedAt: timestamp,
  };
}

export function hasLegacyEventData(state: EventOperationsState): boolean {
  return Boolean(state.event.name.trim() || state.ticketTypes.length || state.attendees.length || state.sessions.length || state.speakers.length);
}

export function createGuidedSetup(state: EventOperationsState, migratedFromLegacy = false, timestamp = nowIso()): GuidedEventSetup {
  const answers: Record<string, GuidedAnswerValue> = {};
  if (migratedFromLegacy) {
    const event = state.event;
    if (event.name) answers['event-name'] = event.name;
    if (event.organizerName) answers['organizer-name'] = event.organizerName;
    if (event.eventType) answers['event-type'] = event.eventType;
    if (event.objective) answers.objective = event.objective;
    if (event.startDate) answers['start-date'] = event.startDate;
    if (event.startTime) answers['start-time'] = event.startTime;
    if (event.endDate) answers['end-date'] = event.endDate;
    if (event.endTime) answers['end-time'] = event.endTime;
    if (event.modality) answers.modality = event.modality;
    if (event.venue) answers.venue = event.venue;
    if (event.city) answers.city = event.city;
    if (event.exactAddress) answers['exact-address'] = event.exactAddress;
    if (event.virtualPlatform) answers['virtual-platform'] = event.virtualPlatform;
    if (event.country) answers.country = event.country;
    if (event.acceptedCurrencies?.length) answers.currency = event.acceptedCurrencies;
    else if (event.defaultCurrency) answers.currency = [event.defaultCurrency];
    if (event.eventFocus?.length) answers['event-focus'] = event.eventFocus;
    if (event.description) answers.description = event.description;
  }
  return {
    version: 1,
    status: 'in_progress',
    currentStepId: 'event-type',
    completedStepIds: Object.keys(answers),
    skippedStepIds: [],
    answers,
    migratedFromLegacy,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function getVisibleGuidedSteps(setup?: GuidedEventSetup): GuidedStepDefinition[] {
  const answers = setup?.answers ?? {};
  const modality = answers.modality;
  const acceptsPayments = answers['accepts-payments'];
  const paymentMethod = answers['payment-method'];
  const hasContacts = answers['has-contacts'];
  const hasProducts = answers['has-products'];
  const businessType = answers['event-type'];
  const isScheduledExperience = ['event', 'congress', 'workshop', 'webinar', 'conference', 'networking'].includes(String(businessType || ''));
  return baseSteps.filter((step) => {
    if (['start-date', 'start-time', 'end-date', 'end-time', 'program'].includes(step.id)) return isScheduledExperience;
    if (step.id === 'city') return modality === 'presential' || modality === 'hybrid';
    if (step.id === 'venue') return modality === 'presential' || modality === 'hybrid';
    if (step.id === 'exact-address') return modality === 'presential' || modality === 'hybrid';
    if (step.id === 'virtual-platform') return modality === 'online' || modality === 'hybrid';
    if (step.id === 'payment-method') return acceptsPayments === 'yes';
    if (step.id === 'manual-payment-url') return acceptsPayments === 'yes' && (paymentMethod === 'manual' || (Array.isArray(paymentMethod) && paymentMethod.includes('manual')));
    if (step.id === 'contact-source') return hasContacts === 'yes';
    if (step.id === 'products') return hasProducts === 'yes';
    return true;
  });
}

function landingFromState(state: EventOperationsState, timestamp: string) {
  const ticketBenefits = state.ticketTypes.flatMap((ticket) => ticket.benefits).filter(Boolean);
  const productBenefits = (state.products ?? []).map((product) => product.name);
  return {
    headline: state.event.name || 'Una solución clara para tu cliente',
    summary: state.event.description || state.event.objective || 'Una propuesta profesional pensada para resolver un problema concreto y generar resultados.',
    organizerBlock: state.event.organizerBio || (state.event.organizerName ? `Ofrecido por ${state.event.organizerName}.` : ''),
    benefits: Array.from(new Set([...ticketBenefits, ...productBenefits, ...(state.event.eventFocus ?? [])])).slice(0, 6),
    callToAction: state.event.eventType === 'event' ? 'Quiero participar' : 'Quiero más información',
    publishingChoice: 'later' as const,
    status: 'draft' as const,
    updatedAt: timestamp,
  };
}

export function synchronizeRegistrationOfferFields(state: EventOperationsState): EventOperationsState {
  const productOptions = (state.products ?? []).filter((item) => item.active).map((item) => item.name);
  const sessionOptions = state.sessions
    .filter((item) => item.status !== 'cancelled' && item.requiresAdditionalRegistration)
    .map((item) => `${item.date} · ${item.startsAt} · ${item.title}`);
  const definitions = [
    { id: 'productSelection', label: 'Curso, taller o producto adicional', options: productOptions },
    { id: 'sessionSelection', label: 'Charlas o actividades con inscripción adicional', options: sessionOptions },
  ];
  let fields = [...state.registrationFields];
  for (const definition of definitions) {
    const existing = fields.find((field) => field.id === definition.id);
    if (existing) {
      fields = fields.map((field) => field.id === definition.id ? { ...field, options: definition.options, active: definition.options.length > 0, required: false } : field);
    } else if (definition.options.length > 0) {
      fields.push({ id: definition.id, label: definition.label, type: 'select', required: false, active: true, options: definition.options, sortOrder: fields.length });
    }
  }
  return { ...state, registrationFields: fields.map((field, index) => ({ ...field, sortOrder: index })) };
}

export function generateContentPlan(state: EventOperationsState): EventContentPlanItem[] {
  const name = state.event.name || 'tu oferta';
  const preferredChannel = state.acquisitionPlan?.channels[0] || 'Email';
  const isScheduledExperience = ['event', 'congress', 'workshop', 'webinar', 'conference', 'networking'].includes(state.event.eventType || '');
  return [
    { id: 'content-authority', title: `El problema que resuelve ${name}`, purpose: 'authority', channel: preferredChannel, status: 'idea' },
    { id: 'content-invitation', title: isScheduledExperience ? `Invitación principal a ${name}` : `Presentación de ${name}`, purpose: 'convocation', channel: preferredChannel, status: 'idea' },
    { id: 'content-benefits', title: 'Resultados, beneficios y forma de trabajo', purpose: 'conversion', channel: 'LinkedIn', status: 'idea' },
    { id: 'content-reminder', title: 'Seguimiento de consultas incompletas', purpose: 'reminder', channel: 'WhatsApp', status: 'idea' },
    { id: 'content-coverage', title: isScheduledExperience ? 'Cobertura en vivo y aprendizajes' : 'Caso, proceso o avance de cliente', purpose: 'coverage', channel: 'Instagram', status: 'idea' },
    { id: 'content-follow-up', title: 'Próximo paso y continuidad', purpose: 'follow_up', channel: 'Email', status: 'idea' },
  ];
}

export function generateEventFunnel(state: EventOperationsState, timestamp = nowIso()): NonNullable<EventOperationsState['funnel']> {
  const hasIdentity = Boolean(state.event.organizerBio || state.event.organizerName);
  const hasAudience = Boolean(state.acquisitionPlan?.audience);
  const hasChannels = Boolean(state.acquisitionPlan?.channels.length);
  const hasForm = state.registrationFields.some((field) => field.active !== false);
  const hasOffer = state.ticketTypes.length > 0;
  return {
    name: `Recorrido de ${state.event.name || 'tu oferta'}`,
    generatedAt: timestamp,
    steps: [
      { id: 'trust', name: 'Confianza', purpose: 'Presentar al organizador y la propuesta', status: hasIdentity ? 'ready' : 'needs_work', source: 'guided_setup' as const },
      { id: 'reach', name: 'Convocatoria', purpose: `Llegar a ${state.acquisitionPlan?.audience || 'la audiencia correcta'}`, status: hasAudience && hasChannels ? 'ready' : 'needs_work', source: 'guided_setup' as const },
      { id: 'decision', name: 'Decisión', purpose: 'Explicar beneficios y formas de acceso', status: hasOffer && state.landingPage ? 'ready' : 'needs_work', source: 'guided_setup' as const },
      { id: 'registration', name: 'Consulta o compra', purpose: 'Capturar solo los datos necesarios', status: hasForm ? 'ready' : 'needs_work', source: 'guided_setup' as const },
      { id: 'attendance', name: 'Entrega', purpose: 'Preparar la prestación del servicio, producto o experiencia', status: state.event.eventType === 'event' ? (state.event.startDate ? 'ready' : 'needs_work') : 'optional', source: 'guided_setup' as const },
      { id: 'follow-up', name: 'Seguimiento', purpose: 'Entregar valor y sostener la relación', status: 'optional' as const, source: 'guided_setup' as const },
    ],
  };
}

export function mapEventPlanToStudio(state: EventOperationsState): ContentItem[] {
  const stageByPurpose: Record<NonNullable<EventOperationsState['contentPlan']>[number]['purpose'], ContentItem['targetStage']> = {
    authority: 'personal', convocation: 'attraction', conversion: 'registration', reminder: 'nurturing', coverage: 'attendance', follow_up: 'retention',
  };
  const formatByPurpose: Record<NonNullable<EventOperationsState['contentPlan']>[number]['purpose'], ContentItem['format']> = {
    authority: 'post', convocation: 'short_video', conversion: 'carousel', reminder: 'email_blast', coverage: 'short_video', follow_up: 'email_blast',
  };
  const supportedPlatforms: ContentItem['platform'][] = ['Instagram', 'TikTok', 'YouTube', 'LinkedIn', 'Meta Ads', 'Email'];
  return (state.contentPlan ?? []).map((item) => ({
    id: `event-${state.event.id}-${item.id}`,
    title: item.title,
    format: formatByPurpose[item.purpose],
    platform: supportedPlatforms.includes(item.channel as ContentItem['platform']) ? item.channel as ContentItem['platform'] : 'Email',
    targetStage: stageByPurpose[item.purpose],
    hook: state.landingPage?.headline || state.event.name,
    bodyScript: state.event.description || state.event.objective,
    callToAction: state.landingPage?.callToAction || 'Quiero participar',
    status: item.status === 'ready' ? 'ready' : item.status === 'published' ? 'published' : 'idea',
    createdAt: state.updatedAt.slice(0, 10),
    tags: ['Oferta', item.purpose, state.event.name].filter(Boolean),
    notes: `Generado desde la configuración guiada de ${state.event.name || 'la oferta'}.`,
  }));
}

export function applyGuidedAnswer(state: EventOperationsState, stepId: string, value: GuidedAnswerValue, timestamp = nowIso()): EventOperationsState {
  const setup = state.guidedSetup ?? createGuidedSetup(state, hasLegacyEventData(state), timestamp);
  const completed = new Set(setup.completedStepIds);
  completed.add(stepId);
  const next: EventOperationsState = {
    ...state,
    event: { ...state.event, updatedAt: timestamp },
    guidedSetup: {
      ...setup,
      status: setup.status === 'not_started' ? 'in_progress' : setup.status,
      answers: { ...setup.answers, [stepId]: value },
      completedStepIds: [...completed],
      skippedStepIds: setup.skippedStepIds.filter((id) => id !== stepId),
      updatedAt: timestamp,
    },
    updatedAt: timestamp,
  };

  const text = typeof value === 'string' ? value : '';
  if (stepId === 'event-name') next.event.name = text;
  if (stepId === 'organizer-name') next.event.organizerName = text;
  if (stepId === 'event-type') next.event.eventType = text;
  if (stepId === 'objective') next.event.objective = text;
  if (stepId === 'start-date') { next.event.startDate = text; if (!next.event.endDate) next.event.endDate = text; }
  if (stepId === 'start-time') next.event.startTime = text;
  if (stepId === 'end-date') next.event.endDate = text;
  if (stepId === 'end-time') next.event.endTime = text;
  if (stepId === 'modality') next.event.modality = text as 'online' | 'presential' | 'hybrid';
  if (stepId === 'venue') next.event.venue = text;
  if (stepId === 'city') next.event.city = text;
  if (stepId === 'exact-address') next.event.exactAddress = text;
  if (stepId === 'virtual-platform') next.event.virtualPlatform = text;
  if (stepId === 'country') next.event.country = text;
  if (stepId === 'currency') {
    const currencies = (Array.isArray(value) ? value : [text]).filter(Boolean) as EventCurrency[];
    next.event.acceptedCurrencies = currencies;
    if (currencies[0]) next.event.defaultCurrency = currencies[0];
  }
  if (stepId === 'event-focus') next.event.eventFocus = Array.isArray(value) ? value : [];
  if (stepId === 'description') next.event.description = text;
  if (stepId === 'organizer-bio') next.event.organizerBio = text;
  if (stepId === 'audience') {
    next.event.targetAudience = text;
    next.acquisitionPlan = { ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }), audience: text };
  }
  if (stepId === 'channels') next.acquisitionPlan = { ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }), channels: Array.isArray(value) ? value : [] };
  if (stepId === 'ad-budget') next.acquisitionPlan = {
    ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }),
    advertisingBudget: Number(value) || 0,
    advertisingCurrency: next.acquisitionPlan?.advertisingCurrency ?? next.event.defaultCurrency,
  };
  if (stepId === 'ad-budget-currency') next.acquisitionPlan = {
    ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }),
    advertisingCurrency: text as EventCurrency,
  };
  if (stepId === 'allies') {
    const allies = text.split(',').map((item) => item.trim()).filter(Boolean);
    next.acquisitionPlan = { ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }), allies };
    next.partners = allies;
  }
  if (stepId === 'primary-action') {
    next.acquisitionPlan = { ...(next.acquisitionPlan ?? { audience: '', channels: [], allies: [], primaryAction: '' }), primaryAction: text, generatedAt: timestamp };
    next.funnel = generateEventFunnel(next, timestamp);
  }
  if (stepId === 'landing' && !next.landingPage) next.landingPage = landingFromState(next, timestamp);
  if (stepId === 'accepts-payments') {
    const accepts = value === 'yes' ? true : value === 'no' ? false : null;
    next.paymentConfiguration = {
      ...(next.paymentConfiguration ?? { method: null, acceptsPayments: null, connectionStatus: 'not_connected', updatedAt: timestamp }),
      acceptsPayments: accepts,
      connectionStatus: accepts === false ? 'not_required' : 'not_connected',
      updatedAt: timestamp,
    };
  }
  if (stepId === 'payment-method') {
    const methods = (Array.isArray(value) ? value : [text]).filter((item): item is 'mercado_pago' | 'stripe' | 'manual' => ['mercado_pago', 'stripe', 'manual'].includes(item));
    next.paymentConfiguration = { ...(next.paymentConfiguration ?? { acceptsPayments: true, connectionStatus: 'not_connected', updatedAt: timestamp }), methods, method: methods[0] ?? null, connectionStatus: 'not_connected', updatedAt: timestamp };
  }
  if (stepId === 'manual-payment-url') {
    next.paymentConfiguration = { ...(next.paymentConfiguration ?? { acceptsPayments: true, method: 'manual', connectionStatus: 'not_connected', updatedAt: timestamp }), manualPaymentUrl: text, connectionStatus: text ? 'manual_ready' : 'not_connected', updatedAt: timestamp };
  }
  if (stepId === 'content-plan') next.contentPlan = generateContentPlan(next);
  return next;
}

export function setGuidedStep(state: EventOperationsState, stepId: string, timestamp = nowIso()): EventOperationsState {
  const setup = state.guidedSetup ?? createGuidedSetup(state, hasLegacyEventData(state), timestamp);
  return { ...state, guidedSetup: { ...setup, currentStepId: stepId, updatedAt: timestamp }, updatedAt: timestamp };
}

export function skipGuidedStep(state: EventOperationsState, stepId: string, timestamp = nowIso()): EventOperationsState {
  const setup = state.guidedSetup ?? createGuidedSetup(state, hasLegacyEventData(state), timestamp);
  return { ...state, guidedSetup: { ...setup, skippedStepIds: Array.from(new Set([...setup.skippedStepIds, stepId])), updatedAt: timestamp }, updatedAt: timestamp };
}

export interface EventReadinessItem { id: string; label: string; status: 'ready' | 'missing' | 'optional'; actionStepId?: string }

export function getEventReadiness(state: EventOperationsState): EventReadinessItem[] {
  const isScheduledExperience = ['event', 'congress', 'workshop', 'webinar', 'conference', 'networking'].includes(state.event.eventType || '');
  const paymentAnswer = state.guidedSetup?.answers['payment-method'];
  const paymentDeferred = state.guidedSetup?.skippedStepIds.includes('payment-method') === true
    || paymentAnswer === 'later'
    || (Array.isArray(paymentAnswer) && paymentAnswer.includes('later'));
  const chosenPaymentMethods = state.paymentConfiguration?.methods?.length
    ? state.paymentConfiguration.methods
    : state.paymentConfiguration?.method && state.paymentConfiguration.method !== 'later'
      ? [state.paymentConfiguration.method]
      : [];
  const paymentReady = state.paymentConfiguration?.acceptsPayments !== true || chosenPaymentMethods.length > 0;
  const hasProductsAnswer = state.guidedSetup?.answers['has-products'];
  const productsReady = hasProductsAnswer !== 'yes' || (state.products ?? []).length > 0;
  return [
    { id: 'identity', label: 'Nombre, tipo de negocio y objetivo', status: state.event.name && state.event.eventType && state.event.objective ? 'ready' : 'missing', actionStepId: 'event-type' },
    { id: 'date', label: 'Inicio, finalización y modalidad', status: !isScheduledExperience ? 'optional' : state.event.startDate && state.event.endDate && state.event.startTime && state.event.endTime && state.event.modality ? 'ready' : 'missing', actionStepId: 'start-date' },
    { id: 'tickets', label: 'Al menos un plan, paquete o forma de compra', status: state.ticketTypes.length > 0 ? 'ready' : 'missing', actionStepId: 'tickets' },
    { id: 'program', label: 'Programa, speakers y horarios', status: isScheduledExperience && state.sessions.length > 0 ? 'ready' : 'optional', actionStepId: 'program' },
    { id: 'participants', label: 'Sponsors, partners y sociedades', status: (state.eventSponsorIds?.length || state.partners?.length) ? 'ready' : 'optional', actionStepId: 'participants' },
    { id: 'landing', label: 'Página pública revisada', status: state.landingPage?.status === 'approved' || state.landingPage?.status === 'published' ? 'ready' : 'missing', actionStepId: 'landing' },
    { id: 'products', label: 'Cursos o productos', status: productsReady ? (hasProductsAnswer === 'yes' ? 'ready' : 'optional') : 'missing', actionStepId: 'products' },
    { id: 'form', label: 'Formulario de contacto o compra', status: state.registrationFields.some((field) => field.active !== false) ? 'ready' : 'missing', actionStepId: 'registration-form' },
    { id: 'payments', label: paymentDeferred ? 'Cobro para configurar después' : 'Cobro configurado', status: paymentDeferred ? 'optional' : paymentReady ? 'ready' : 'missing', actionStepId: 'payment-method' },
    { id: 'payment-connection', label: 'Conexión automática de cobro', status: state.paymentConfiguration?.acceptsPayments === true && state.paymentConfiguration.connectionStatus !== 'connected' ? 'optional' : 'ready' },
    { id: 'acquisition', label: 'Audiencia y canal de captación', status: state.acquisitionPlan?.audience && state.acquisitionPlan.channels.length ? 'ready' : 'missing', actionStepId: 'audience' },
    { id: 'contacts', label: 'Lista inicial de personas', status: state.attendees.length ? 'ready' : 'optional' },
    { id: 'speakers', label: 'Speakers confirmados', status: state.speakers.length ? 'ready' : 'optional' },
  ];
}

export function activateEvent(state: EventOperationsState, timestamp = nowIso()): EventOperationsState {
  const setup = state.guidedSetup ?? createGuidedSetup(state, hasLegacyEventData(state), timestamp);
  return {
    ...state,
    event: { ...state.event, status: 'active', updatedAt: timestamp },
    funnel: generateEventFunnel(state, timestamp),
    guidedSetup: { ...setup, status: 'active', activatedAt: timestamp, updatedAt: timestamp },
    updatedAt: timestamp,
  };
}

export function guidedProgress(state: EventOperationsState): number {
  const visible = getVisibleGuidedSteps(state.guidedSetup);
  const completed = new Set([...(state.guidedSetup?.completedStepIds ?? []), ...(state.guidedSetup?.skippedStepIds ?? [])]);
  return visible.length ? Math.round((visible.filter((step) => completed.has(step.id)).length / visible.length) * 100) : 0;
}
